import { CartAddEvent } from '@theme/events';

/**
 * Clones the inner `<template>` (quick-order sheet markup) into `document.body` when the trigger button is clicked.
 */
class QuickAddOrderSheet extends HTMLElement {
  /** @type {AbortController | null} */
  #hostAbort = null;
  /** @type {HTMLElement | null} */
  #mountedRoot = null;
  /** @type {((e: KeyboardEvent) => void) | null} */
  #onDocumentKeydown = null;
  /** @type {string} */
  #prevHtmlOverflow = '';
  /** @type {AbortController | null} */
  #mountListenersAbort = null;

  connectedCallback() {
    this.#hostAbort = new AbortController();
    const { signal } = this.#hostAbort;
    this.addEventListener('click', this.#onHostClick, { signal });
  }

  disconnectedCallback() {
    this.#hostAbort?.abort();
    this.#hostAbort = null;
    this.#teardownMount();
  }

  /** @param {MouseEvent} event */
  #onHostClick = (event) => {
    const trigger = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-quick-order-sheet-trigger]') : null
    );
    if (!trigger || !this.contains(trigger)) return;
    event.preventDefault();
    event.stopPropagation();
    this.#open();
  };

  #open() {
    const template = this.querySelector('template');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.#teardownMount();

    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const root = /** @type {HTMLElement | null} */ (fragment.querySelector('.quick-order-sheet'));
    if (!root) return;

    document.body.appendChild(fragment);
    this.#mountedRoot = root;

    this.#prevHtmlOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    this.#onDocumentKeydown = (e) => {
      if (e.key === 'Escape') this.#close();
    };
    document.addEventListener('keydown', this.#onDocumentKeydown);

    const closeBtn = root.querySelector('.quick-order-sheet__close');
    closeBtn?.addEventListener('click', () => this.#close(), { once: true });

    root.querySelector('.quick-order-sheet__overlay')?.addEventListener(
      'click',
      () => this.#close(),
      { once: true }
    );

    this.#bindPurchaseMode(root);

    this.#mountListenersAbort = new AbortController();
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd, {
      signal: this.#mountListenersAbort.signal,
    });

    requestAnimationFrame(() => {
      root.setAttribute('open', '');
    });

    const focusTarget = closeBtn instanceof HTMLElement ? closeBtn : root;
    focusTarget.focus({ preventScroll: true });
  }

  /**
   * Pre-order radio: hide default ATC, show pre-order CTA + explainer. Buy-now radio: reverse.
   * @param {HTMLElement} root
   */
  #bindPurchaseMode(root) {
    const preorderVariantId = root.dataset.qosPreorderVariant;
    if (!preorderVariantId) return;

    const sync = () => {
      const checked = root.querySelector('input[name="quick-order-purchase"]:checked');
      if (!(checked instanceof HTMLInputElement)) return;

      const isPreorder = checked.value === preorderVariantId;

      const buyWrap = root.querySelector('[data-qos-atc-wrap="buy"]');
      const preorderWrap = root.querySelector('[data-qos-atc-wrap="preorder"]');
      const explainer = root.querySelector('.quick-order-sheet__explainer');

      if (buyWrap instanceof HTMLElement) buyWrap.hidden = isPreorder;
      if (preorderWrap instanceof HTMLElement) preorderWrap.hidden = !isPreorder;
      if (explainer instanceof HTMLElement) explainer.hidden = !isPreorder;

      root.querySelectorAll('.quick-order-sheet__option').forEach((label) => {
        const input = label.querySelector('input[name="quick-order-purchase"]');
        const checked = input instanceof HTMLInputElement && input.checked;
        label.classList.toggle('quick-order-sheet__option--selected', checked);
      });
    };

    root.querySelectorAll('input[name="quick-order-purchase"]').forEach((input) => {
      input.addEventListener('change', sync);
    });
    sync();
  }

  /** @param {Event} event */
  #onCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;
    const root = this.#mountedRoot;
    if (!root) return;
    const target = event.target;
    if (!(target instanceof Node) || !root.contains(target)) return;
    if (event.detail?.data?.didError) return;
    this.#close();
  };

  #close() {
    const root = this.#mountedRoot;
    if (!root || root.dataset.qosClosing === 'true') return;
    root.dataset.qosClosing = 'true';

    this.#mountListenersAbort?.abort();
    this.#mountListenersAbort = null;

    root.removeAttribute('open');

    const panel = root.querySelector('.quick-order-sheet__panel');
    let finished = false;
    const removeAfterTransition = () => {
      if (finished) return;
      finished = true;
      root.remove();
      if (this.#mountedRoot === root) this.#mountedRoot = null;
      document.documentElement.style.overflow = this.#prevHtmlOverflow;
      if (this.#onDocumentKeydown) {
        document.removeEventListener('keydown', this.#onDocumentKeydown);
        this.#onDocumentKeydown = null;
      }
    };

    if (panel instanceof HTMLElement) {
      const t = setTimeout(removeAfterTransition, 400);
      panel.addEventListener(
        'transitionend',
        () => {
          clearTimeout(t);
          removeAfterTransition();
        },
        { once: true }
      );
    } else {
      removeAfterTransition();
    }
  }

  #teardownMount() {
    this.#mountListenersAbort?.abort();
    this.#mountListenersAbort = null;

    if (this.#onDocumentKeydown) {
      document.removeEventListener('keydown', this.#onDocumentKeydown);
      this.#onDocumentKeydown = null;
    }
    document.documentElement.style.overflow = this.#prevHtmlOverflow;

    this.#mountedRoot?.remove();
    this.#mountedRoot = null;
  }
}

if (!customElements.get('quick-add-order-sheet')) {
  customElements.define('quick-add-order-sheet', QuickAddOrderSheet);
}
